import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const ConsentPolicyPage = () => {
  return (
    <div className="min-h-screen bg-brand-bg">
      <header className="border-b border-brand-border bg-brand-surface">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="font-serif text-xl text-brand-text">
            Afro <span className="text-brand-gold">Magic</span>
          </Link>
          <Link to="/" className="flex items-center gap-2 font-sans text-sm text-brand-text-muted hover:text-brand-gold transition-colors">
            <ArrowLeft size={16} /> Back to Home
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12 md:py-20">
        <h1 className="font-serif text-3xl md:text-4xl text-brand-text mb-2">Privacy & SMS Consent Policy</h1>
        <p className="font-sans text-sm text-brand-text-muted mb-10">Last updated: April 2026</p>

        <div className="space-y-8 font-sans text-sm text-brand-text-muted leading-relaxed">
          <section>
            <h2 className="font-serif text-xl text-brand-text mb-3">1. Business Information</h2>
            <p>
              <strong className="text-brand-text">Afro Magic Braiding Hair</strong><br />
              22560 Telegraph Rd, Southfield, MI 48033<br />
              Phone: (313) 484-7307<br />
              Email: booking@afromagicbraiding.shop<br />
              Website: https://afromagicbraiding.shop
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-brand-text mb-3">2. SMS Messaging Consent</h2>
            <p className="mb-3">
              By checking the SMS consent box on our booking form or walk-in form, you agree to receive automated text messages from Afro Magic Braiding Hair. These messages may include:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Appointment confirmations and reminders</li>
              <li>Service updates and follow-up care instructions</li>
              <li>Promotional offers, discounts, and special deals</li>
              <li>Important business announcements</li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif text-xl text-brand-text mb-3">3. How We Collect Consent</h2>
            <p className="mb-3">
              Customers opt-in by visiting our website, https://afromagicbraiding.shop, and entering their phone number into our booking form. Before submitting, they must check a clear box that states:
            </p>
            <div className="border border-brand-gold/30 bg-brand-surface p-4 my-4">
              <p className="text-brand-text italic">
                "I agree to receive automated appointment reminders and service updates via SMS from Afro Magic Braiding. Message and data rates may apply. Reply STOP to opt-out, HELP for help."
              </p>
            </div>
            <p>
              We also collect verbal consent for follow-up care instructions during in-person appointments at our salon.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-brand-text mb-3">4. Message Frequency</h2>
            <p>
              Message frequency varies. You may receive up to 4 messages per month, including appointment reminders and occasional promotional offers. Message and data rates may apply depending on your carrier.
            </p>
          </section>

          <section data-testid="sms-marketing-policy-section">
            <h2 className="font-serif text-xl text-brand-text mb-3">4a. SMS Marketing Program</h2>
            <p className="mb-3">
              Separately from appointment reminders, clients may choose to join our SMS marketing program by checking the optional promotional-texts box on the booking form, or by replying <strong className="text-brand-text">YES</strong> to an invitation text.
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong className="text-brand-text">Types of messages:</strong> service specials (like our Wednesday Silk Press Special), new product announcements, online-store offers, seasonal promotions, and occasional re-booking offers.</li>
              <li><strong className="text-brand-text">Message and data rates may apply</strong> depending on your mobile carrier and plan.</li>
              <li><strong className="text-brand-text">Consent is not a condition of purchase.</strong> You can book and receive all salon services without joining the marketing program, and you will still receive normal appointment reminders if you consented to those.</li>
              <li><strong className="text-brand-text">Opt out anytime:</strong> reply <strong className="text-brand-text">STOP</strong> to any message. Promotional texts stop immediately and you receive one final confirmation text.</li>
              <li><strong className="text-brand-text">Need help?</strong> Reply <strong className="text-brand-text">HELP</strong> to any message, call (313) 484-7307, or email booking@afromagicbraiding.shop.</li>
            </ul>
            <p className="mt-3">
              We record the date, time, phone number, and source of every consent and every opt-out request. We never enroll anyone in promotional texts without their affirmative consent, and we never share your phone number with third parties for their marketing.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-brand-text mb-3">5. How to Opt-Out</h2>
            <p className="mb-3">
              You can opt out of receiving SMS messages at any time by:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Replying <strong className="text-brand-text">STOP</strong> to any message you receive from us</li>
              <li>Calling us at (313) 484-7307</li>
              <li>Emailing booking@afromagicbraiding.shop with "UNSUBSCRIBE" in the subject line</li>
            </ul>
            <p className="mt-3">
              After opting out, you will receive one final confirmation message and no further texts will be sent.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-brand-text mb-3">6. Help</h2>
            <p>
              For help or questions about our SMS program, reply <strong className="text-brand-text">HELP</strong> to any message, call (313) 484-7307, or email booking@afromagicbraiding.shop.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-brand-text mb-3">7. Information We Collect</h2>
            <p className="mb-3">When you use our booking or walk-in forms, we collect:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Full name</li>
              <li>Phone number</li>
              <li>Email address (optional)</li>
              <li>Service preferences</li>
            </ul>
            <p className="mt-3">
              We do not sell, rent, or share your personal information with third parties for marketing purposes. Your phone number and personal information collected through our SMS consent process <strong className="text-brand-text">will not be shared with any third parties.</strong> Your information is used solely to provide our hair braiding services, send appointment communications, and occasional promotional offers.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-brand-text mb-3">8. Data Security</h2>
            <p>
              We use industry-standard security measures to protect your personal information, including encrypted connections (HTTPS) and secure payment processing through Stripe.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-brand-text mb-3">9. Supported Carriers</h2>
            <p>
              Our SMS service is compatible with all major US carriers including AT&T, Verizon, T-Mobile, Sprint, and more. Carriers are not liable for delayed or undelivered messages.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-xl text-brand-text mb-3">10. Contact Us</h2>
            <p>
              If you have any questions about this policy, please contact us:<br /><br />
              <strong className="text-brand-text">Afro Magic Braiding Hair</strong><br />
              22560 Telegraph Rd, Southfield, MI 48033<br />
              Phone: (313) 484-7307<br />
              Email: booking@afromagicbraiding.shop
            </p>
          </section>
        </div>
      </main>

      <footer className="border-t border-brand-border py-6">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="font-sans text-xs text-brand-text-muted/60">&copy; {new Date().getFullYear()} Afro Magic Braiding Hair. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default ConsentPolicyPage;
